import * as React from "react";
import { useMinimumWageQuery } from "../useMinimumWageQuery";
import { MinimalWagePageProps } from "../types";
import { useMinimumWageMutation } from "../useMinimumWageMutation";
import { useLocalize } from "@/modules/shared/context/localize/LocalizeContext";
import { MinimumWageCountryLayout } from "../components/MinimumWageCountryLayout";
import { FieldName, MinimumWageOptionsSection } from "../containers/MinimumWageOptionsSection";
import { MinimumWageAllowanceExpandableItemSection } from "../containers/MinimumWageAllowanceExpandableItemSection";
import { CalculationStartDateSection } from "../containers/CalculationStartDateSection";
import { MinimumWagePolishOptionsSection } from "../containers/MinimumWagePolishOptionsSection";
import { GenericMinimumWageExpandableItemSection } from "../containers/GenericMinimumWageExpandableItemSection";
import { DriverQualificationsType } from "@/app/constants/driverQualificationsType";
import { DriverWorkYearsPeriods } from "@/app/constants/configuration/driverWorkYearsPeriods";
import { FinlandMinimumWageConfigurationModel } from "@/app/models/configuration/minimumWage/finlandMinimumWageConfigurationModel";

import { PreLoader } from "../components/PreLoader"
import { buildMinimumWageDiffRenderers } from '../minimumWageDiffUtils';

const qualificationTypes = [
  DriverQualificationsType.NotSet,
  DriverQualificationsType.FinlandBusDriver,
  DriverQualificationsType.TrackDriverWithTrailer,
  DriverQualificationsType.TractorDriver,
  DriverQualificationsType.RegularDriver,
]

const driverWorkYears = [
  DriverWorkYearsPeriods.All,
  DriverWorkYearsPeriods.None,
  DriverWorkYearsPeriods.Below4,
  DriverWorkYearsPeriods.Above4Below8,
  DriverWorkYearsPeriods.Above8Below12,
  DriverWorkYearsPeriods.Above12,
]

const defaultFields: FieldName[] = [
    "printCalculatedHours",
    "calculateCabotageAndCrossTrade",
]

const allowedCurrenciesInner = [
    'EUR',
]

const allowedCurrencies = [
    'EUR',
    'PLN',
]

const country = 'finland'

export const FinlandMinimumWageSettingsPage = ({ match }: MinimalWagePageProps) => {
    const { companyId } = match.params
    const q = useMinimumWageQuery(companyId)
    const up = useMinimumWageMutation(companyId)
    const { t } = useLocalize()

    const [state, setState] = React.useState<FinlandMinimumWageConfigurationModel | null>(null)

    React.useEffect(() => {
        setState(null)
    }, [companyId])

    React.useEffect(() => {
        if (q.data) {
            const newState = structuredClone(q.data.finlandConfiguration)
            setState(newState)
        }
    }, [q.data])

    const diffRenderers = React.useMemo(() => buildMinimumWageDiffRenderers('finlandConfiguration', t), [t])
    const diffLabels = React.useMemo<Record<string, string>>(() => ({
        configuration: t('configuration.minimumWages.options'),
        expandableItems: t('configuration.minimumWages.minimalHourRateTitle'),
        allowanceExpandableItems: t('configuration.minimumWages.minimalAllowanceTitle'),
    }), [t])

    if (!state || !q.data) {
        return <PreLoader />
    }

    const isDirty = JSON.stringify(state) !== JSON.stringify(q.data.finlandConfiguration)

    return (
        <MinimumWageCountryLayout
            selectedCountryValue={country}
            isDirty={isDirty}
            handleSave={() => up.mutateAsync({ ...q.data, finlandConfiguration: state })}
            handleCancel={() => setState(q.data.finlandConfiguration)}
            dataTestId="finland-minimum-wage-settings-page"
            isLoading={(q.isFetching || up.isPending)}
            diffBefore={q.data.finlandConfiguration as Record<string, unknown>}
            diffAfter={state as Record<string, unknown>}
            diffLabels={diffLabels}
            diffRenderers={diffRenderers}
        >
            <CalculationStartDateSection
                state={state}
                setState={setState}
            />

            <div className={!state.configuration.calculateAmountsForCountry ? 'tw-opacity-50 tw-pointer-events-none' : ''}>
                    <GenericMinimumWageExpandableItemSection
                        allowedCurrencies={allowedCurrenciesInner}
                        qualificationTypes={qualificationTypes} 
                        driverWorkYears={driverWorkYears}
                        state={state}
                        setState={setState}
                    />

                    <MinimumWageAllowanceExpandableItemSection
                        allowedCurrencies={allowedCurrencies}
                        state={state}
                        setState={setState}
                    />

                    <MinimumWageOptionsSection
                        className='tw-my-5'
                        loading={false}
                        fields={defaultFields}
                        translateKey='finlandConfiguration'
                        state={state}
                        accommodationAndAllowanceMaxDate={q.data.generalConfiguration.accommodationAndAllowanceMaxDate}
                        setState={setState}
                    />

                    <MinimumWagePolishOptionsSection 
                        className='tw-my-5'
                        loading={false}
                        state={state}
                        setState={setState}
                    />
            </div>

            <div style={{ minHeight: '90px' }} />
        </MinimumWageCountryLayout>
    )
}
